/**
 * Bulk delete and bulk duplicate for the collections sidebar — the one place
 * that loops.
 *
 * ── Why this exists at all, rather than "the sidebar just calls delete N times"
 * Every single-item delete toasts on success (`toast.requestDeleted`,
 * `toast.collectionDeleted`, `toast.folderDeleted`) and again on failure. A naive
 * loop over a 50-row post-import triage therefore ships FIFTY toasts, which is
 * the single most likely way this feature ships feeling broken. So the loop lives
 * here, runs each existing single-item path with `{ silent: true }`, counts what
 * failed, and emits exactly ONE summary toast.
 *
 * The single-item paths are otherwise untouched: same order of operations, same
 * HTTP calls, same tab/selection cleanup, same messages when called normally.
 *
 * ── Ordering
 * The caller hands over an already-NORMALISED list (no node whose ancestor is
 * also in it), so the entries are disjoint subtrees and the order between them
 * carries no correctness weight. Collections go last only so that a failure
 * mid-way leaves the smaller things gone rather than the container.
 */

import { useCallback } from 'react';

export function useBulkCollectionActions({
  deleteRequest,
  deleteCollection,
  deleteFolder,
  duplicateRequest,
  duplicateCollection,
  duplicateFolder,
  toastT,
}) {
  const bulkDelete = useCallback(
    async (entries) => {
      const items = Array.isArray(entries) ? entries : [];
      if (items.length === 0) return { total: 0, failed: 0 };

      const ordered = [
        ...items.filter((e) => e.kind === 'request'),
        ...items.filter((e) => e.kind === 'folder'),
        ...items.filter((e) => e.kind === 'collection'),
      ];

      let failed = 0;
      for (const entry of ordered) {
        let ok = false;
        try {
          if (entry.kind === 'request') {
            ok = await deleteRequest?.(entry.item, { silent: true });
          } else if (entry.kind === 'folder') {
            ok = await deleteFolder?.(entry.item.collectionId, entry.item, { silent: true });
          } else {
            ok = await deleteCollection?.(entry.item, { silent: true });
          }
        } catch (error) {
          console.error('Bulk delete failed for', entry.id, error);
          ok = false;
        }
        if (ok === false) failed += 1;
      }

      const total = ordered.length;
      if (failed > 0) {
        toastT?.('toast.bulkDeletePartial', 'error', {
          done: total - failed,
          total,
          failed,
        });
      } else {
        toastT?.('toast.bulkDeleted', 'success', { count: total });
      }
      return { total, failed };
    },
    [deleteRequest, deleteCollection, deleteFolder, toastT]
  );

  /**
   * Duplicate every entry of an already-NORMALISED selection (see the header:
   * no node whose ancestor is also in it — duplicating {folder-7, request-42∈7}
   * copies the folder once, and 42 rides along inside the copy rather than also
   * getting a second copy next to the original).
   *
   * Sequential on purpose. Each single-item path reads the store, writes the
   * store and — in a cloud workspace — POSTs; run in parallel they would build
   * on the same snapshot and the last writer would drop the others' rows.
   *
   * A plan limit stops the run: every later item would hit the same wall, and
   * the single-item path has already opened the upgrade modal by then.
   */
  const bulkDuplicate = useCallback(
    async (entries) => {
      const items = Array.isArray(entries) ? entries : [];
      if (items.length === 0) return { total: 0, failed: 0 };

      let failed = 0;
      let done = 0;
      for (const entry of items) {
        let ok = false;
        try {
          if (entry.kind === 'request') {
            ok = await duplicateRequest?.(entry.item, { silent: true });
          } else if (entry.kind === 'folder') {
            ok = await duplicateFolder?.(entry.collection, entry.item, { silent: true });
          } else {
            ok = await duplicateCollection?.(entry.item, { silent: true });
          }
        } catch (error) {
          console.error('Bulk duplicate failed for', entry.id, error);
          ok = false;
        }
        if (ok === false) failed += 1;
        else done += 1;
      }

      const total = items.length;
      if (failed > 0) {
        toastT?.('toast.bulkDuplicatePartial', 'error', { done, total, failed });
      } else {
        toastT?.('toast.bulkDuplicated', 'success', { count: total });
      }
      return { total, failed };
    },
    [duplicateRequest, duplicateCollection, duplicateFolder, toastT]
  );

  return { bulkDelete, bulkDuplicate };
}
